import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Measurement, MeteoSchweizData } from '../types';
import { getSwissTimeComponents } from '../utils/timezoneUtils';

interface TemperatureDevelopmentChartProps {
  measurements: Measurement[];
  meteoSchweizData?: MeteoSchweizData[];
  showMeteoSchweiz?: boolean;
  title: string;
}

interface ChartDataPoint {
  time: string;
  measured: number;
  perceived: number;
  meteoSchweiz: number;
  measuredCount: number;
  perceivedCount: number;
  measuredStdDev: number;
  perceivedComfort: string;
}

const TemperatureDevelopmentChart: React.FC<TemperatureDevelopmentChartProps> = ({ 
  measurements, 
  meteoSchweizData = [],
  showMeteoSchweiz = false,
  title
}) => {
  const [chartType, setChartType] = useState<'line' | 'area'>('line');



  // Generate time slots from 6:00 to 22:00 in 30-minute intervals
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  }, []);

  // Calculate comprehensive statistics for each time slot
  const chartData = useMemo((): ChartDataPoint[] => {
    const slotData: { [key: string]: { measured: number[], perceived: string[], meteoSchweiz: number[] } } = {};
    
    // Initialize time slots
    timeSlots.forEach(slot => {
      slotData[slot] = { measured: [], perceived: [], meteoSchweiz: [] };
    });

    // Group measurements by time slot (using Swiss time)
    measurements.forEach(measurement => {
      const swissTimeComponents = getSwissTimeComponents(measurement.created_at);
      const hour = swissTimeComponents.hours;
      const minute = swissTimeComponents.minutes;
      const slot = `${hour.toString().padStart(2, '0')}:${(minute < 30 ? 0 : 30).toString().padStart(2, '0')}`;
      
      if (slotData[slot]) {
        // Add measured temperature
        if (measurement.measured_temperature > 0) {
          slotData[slot].measured.push(measurement.measured_temperature);
        }
        
        // Add perceived comfort level
        if (measurement.perceived_temperature) {
          slotData[slot].perceived.push(measurement.perceived_temperature);
        }
      }
    });

    // Group MeteoSchweiz data by time slot with interpolation
    if (showMeteoSchweiz && meteoSchweizData.length > 0) {
      // Sort MeteoSchweiz data by timestamp
      const sortedMeteoData = meteoSchweizData
        .filter(data => data.tre200h0 > 0)
        .sort((a, b) => new Date(a.reference_timestamp).getTime() - new Date(b.reference_timestamp).getTime());

      // Process each time slot and interpolate MeteoSchweiz data
      timeSlots.forEach(slot => {
        const [slotHour, slotMinute] = slot.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;
        
        // Find the two closest hourly MeteoSchweiz readings
        let beforeReading: MeteoSchweizData | null = null;
        let afterReading: MeteoSchweizData | null = null;
        
        for (let i = 0; i < sortedMeteoData.length - 1; i++) {
          const current = sortedMeteoData[i];
          const next = sortedMeteoData[i + 1];
          
          const currentTime = new Date(current.reference_timestamp);
          const nextTime = new Date(next.reference_timestamp);
          
          const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
          const nextMinutes = nextTime.getHours() * 60 + nextTime.getMinutes();
          
          if (slotTimeInMinutes >= currentMinutes && slotTimeInMinutes <= nextMinutes) {
            beforeReading = current;
            afterReading = next;
            break;
          }
        }
        
        // Interpolate temperature for this time slot
        if (beforeReading && afterReading) {
          const beforeTime = new Date(beforeReading.reference_timestamp);
          const afterTime = new Date(afterReading.reference_timestamp);
          
          const beforeMinutes = beforeTime.getHours() * 60 + beforeTime.getMinutes();
          const afterMinutes = afterTime.getHours() * 60 + afterTime.getMinutes();
          
          const beforeTemp = beforeReading.tre200h0;
          const afterTemp = afterReading.tre200h0;
          
          // Linear interpolation
          const timeRatio = (slotTimeInMinutes - beforeMinutes) / (afterMinutes - beforeMinutes);
          const interpolatedTemp = beforeTemp + (afterTemp - beforeTemp) * timeRatio;
          
          slotData[slot].meteoSchweiz.push(interpolatedTemp);
        }
      });
    }

    // Calculate statistics for each time slot
    return timeSlots.map(slot => {
      const measuredTemps = slotData[slot].measured;
      const perceivedComforts = slotData[slot].perceived;

      // Calculate measured temperature statistics
      const measuredAvg = measuredTemps.length > 0 
        ? measuredTemps.reduce((a, b) => a + b, 0) / measuredTemps.length 
        : 0;
      
      const measuredStdDev = measuredTemps.length > 1
        ? Math.sqrt(measuredTemps.reduce((sum, temp) => sum + Math.pow(temp - measuredAvg, 2), 0) / (measuredTemps.length - 1))
        : 0;

      // Calculate perceived comfort index
      const comfortCounts: { [key: string]: number } = {};
      perceivedComforts.forEach(comfort => {
        comfortCounts[comfort] = (comfortCounts[comfort] || 0) + 1;
      });
      
      const mostCommonComfort = Object.keys(comfortCounts).length > 0
        ? Object.entries(comfortCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
        : '';

      // Convert comfort level to a numerical index for charting
      // Based on official project temperature scale
      const getComfortIndex = (comfort: string): number => {
        const comfortMap: { [key: string]: number } = {
          'Sehr kalt': 4,      // < 6°C
          'Kalt': 8,           // 6-11°C
          'Kühl': 13,          // 11-15°C
          'Leicht kühl': 18,   // 15-19°C
          'Behaglich': 20,     // 19-22°C
          'Leicht warm': 23,   // 22-26°C
          'Warm': 29,          // 26-32°C
          'Heiss': 35,         // 32-37°C
          'Sehr heiss': 41,    // > 37°C
        };
        return comfortMap[comfort] || 0;
      };

      // Calculate MeteoSchweiz average for this time slot
      const meteoSchweizTemps = slotData[slot].meteoSchweiz;
      const meteoSchweizAvg = meteoSchweizTemps.length > 0 
        ? meteoSchweizTemps.reduce((a, b) => a + b, 0) / meteoSchweizTemps.length 
        : 0;

      return {
        time: slot,
        measured: Math.round(measuredAvg * 10) / 10,
        perceived: getComfortIndex(mostCommonComfort),
        meteoSchweiz: Math.round(meteoSchweizAvg * 10) / 10,
        measuredCount: measuredTemps.length,
        perceivedCount: perceivedComforts.length,
        measuredStdDev: Math.round(measuredStdDev * 10) / 10,
        perceivedComfort: mostCommonComfort,
      };
    });
  }, [measurements, meteoSchweizData, showMeteoSchweiz, timeSlots]);



  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
                      <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-600">Gemessen:</span>
                <span className="font-medium">{data.measured}°C</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600">Gefühlt:</span>
                <span className="font-medium">{data.perceivedComfort || 'Keine Daten'}</span>
              </div>
              {showMeteoSchweiz && data.meteoSchweiz > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">MeteoSchweiz:</span>
                  <span className="font-medium">{data.meteoSchweiz}°C</span>
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                {data.measuredCount} gemessene, {data.perceivedCount} gefühlte Werte
                {showMeteoSchweiz && data.meteoSchweiz > 0 && `, MeteoSchweiz verfügbar`}
              </div>
            </div>
        </div>
      );
    }
    return null;
  };

  // Shorten weather condition description for display
  const shortenWeatherDescription = (weather: string): string => {
    const weatherMap: { [key: string]: string } = {
      'Die Sonne scheint (sehr starke Sonneneinstrahlung)': 'Starke Sonne',
      'Die Sonne scheint, aber es hat vereinzelt Wolken': 'Sonne & Wolken',
      'Ein leichter Wind weht': 'Leichter Wind',
      'Es ist stark bewölkt': 'Stark bewölkt',
      'Es ist windstill': 'Windstill',
      'Es regnet': 'Regen',
      'Es hat vor weniger als zwei Stunden geregnet': 'Vor kurzem geregnet',
      'Ein starker Wind weht': 'Starker Wind',
    };
    return weatherMap[weather] || weather;
  };

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const allMeasured = measurements
      .map(m => m.measured_temperature)
      .filter(t => t > 0);
    
    const allPerceived = measurements
      .map(m => m.perceived_temperature)
      .filter(t => t && t.trim() !== '');

    const measuredAvg = allMeasured.length > 0 
      ? allMeasured.reduce((a, b) => a + b, 0) / allMeasured.length 
      : 0;
    
    const measuredMin = allMeasured.length > 0 
      ? Math.min(...allMeasured)
      : 0;
    
    const measuredMax = allMeasured.length > 0 
      ? Math.max(...allMeasured)
      : 0;

    // Get most common perceived comfort level
    const comfortCounts: { [key: string]: number } = {};
    allPerceived.forEach(comfort => {
      comfortCounts[comfort] = (comfortCounts[comfort] || 0) + 1;
    });
    const mostCommonComfort = Object.keys(comfortCounts).length > 0
      ? Object.entries(comfortCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
      : '';

    // Get most common weather condition
    const allWeatherConditions = measurements
      .flatMap(m => m.weather_conditions || [])
      .filter(wc => wc && wc.trim() !== '');

    const weatherCounts: { [key: string]: number } = {};
    allWeatherConditions.forEach(condition => {
      weatherCounts[condition] = (weatherCounts[condition] || 0) + 1;
    });
    const mostCommonWeather = Object.keys(weatherCounts).length > 0
      ? Object.entries(weatherCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0]
      : '';

    return {
      measuredAvg: Math.round(measuredAvg * 10) / 10,
      measuredMin: Math.round(measuredMin * 10) / 10,
      measuredMax: Math.round(measuredMax * 10) / 10,
      measuredCount: allMeasured.length,
      perceivedCount: allPerceived.length,
      mostCommonComfort,
      mostCommonWeather,
    };
  }, [measurements]);

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 md:mb-0">{title}</h3>
        <div className="flex space-x-2 md:justify-end">
          <button
            onClick={() => setChartType('line')}
            className={`flex-1 md:flex-none px-3 py-1 rounded text-sm ${
              chartType === 'line' 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Linien
          </button>
          <button
            onClick={() => setChartType('area')}
            className={`flex-1 md:flex-none px-3 py-1 rounded text-sm ${
              chartType === 'area' 
                ? 'bg-primary text-white' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Flächen
          </button>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-sm text-blue-600 font-medium">Ø Gemessen</div>
          <div className="text-xl font-bold text-blue-800">{overallStats.measuredAvg}°C</div>
          <div className="text-xs text-blue-600">
            Min. {overallStats.measuredMin}°C / Max {overallStats.measuredMax}°C
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <div className="text-sm text-orange-600 font-medium">Häufigste Empfindung</div>
          <div className="text-xl font-bold text-orange-800">{overallStats.mostCommonComfort || 'Keine Daten'}</div>
          <div className="text-xs text-orange-600">{overallStats.perceivedCount} Bewertungen</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-sm text-green-600 font-medium">Wetterbedingungen</div>
          <div className="text-xl font-bold text-green-800">
            {overallStats.mostCommonWeather ? shortenWeatherDescription(overallStats.mostCommonWeather) : 'Keine Daten'}
          </div>
          <div className="text-xs text-green-600">Häufigste Bedingung</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600 font-medium">Zeitraum</div>
          <div className="text-xl font-bold text-gray-800">
            {measurements.length > 0 ? `${measurements.length} Messungen` : 'Keine Daten'}
          </div>
          <div className="text-xs text-gray-600">Gesamtanzahl</div>
        </div>
      </div>
      


      {/* Chart */}
      <div className="h-80 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                label={{ value: 'Temperatur (°C)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                domain={[0, 'dataMax + 5']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                align="left"
                className="md:!text-center"
              />
              <Line 
                type="monotone"
                dataKey="measured" 
                name="Gemessene Temperatur"
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#ffffff' }}
              />
              <Line 
                type="monotone"
                dataKey="perceived" 
                name="Empfundene Temperatur"
                stroke="#f97316" 
                strokeWidth={3}
                dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#f97316', strokeWidth: 2, fill: '#ffffff' }}
              />
              {showMeteoSchweiz && (
                <Line 
                  type="monotone"
                  dataKey="meteoSchweiz" 
                  name="MeteoSchweiz Daten"
                  stroke="#6b7280" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  strokeOpacity={0.7}
                  dot={false}
                />
              )}
            </LineChart>
          ) : (
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={{ stroke: '#e5e7eb' }}
                label={{ value: 'Temperatur (°C)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                domain={[0, 'dataMax + 5']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="circle"
                align="left"
                className="md:!text-center"
              />
              <Area 
                type="monotone"
                dataKey="measured" 
                name="Gemessene Temperatur"
                stroke="#3b82f6" 
                fill="#3b82f6"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Area 
                type="monotone"
                dataKey="perceived" 
                name="Empfundene Temperatur"
                stroke="#f97316" 
                fill="#f97316"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              {showMeteoSchweiz && (
                <Area 
                  type="monotone"
                  dataKey="meteoSchweiz" 
                  name="MeteoSchweiz Daten"
                  stroke="#6b7280" 
                  fill="#6b7280"
                  fillOpacity={0.15}
                  strokeOpacity={0.7}
                  strokeWidth={1.5}
                />
              )}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>

             {/* Data Explanation */}
       <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
         <h4 className="text-sm font-semibold text-gray-700 mb-2">Daten-Erklärung</h4>
         <div className="text-xs text-gray-600 space-y-1">
           <p>
             <strong>Gemessene Temperatur:</strong> Physikalische Temperaturmessung mit Sensoren (in °C)
           </p>
           <p>
             <strong>Empfundene Temperatur:</strong> Häufigste subjektive Temperaturempfindung pro Zeitraum
           </p>
           <p>
             <strong>MeteoSchweiz Daten:</strong> Offizielle Temperaturdaten der Schweizer Wetterbehörde für Vergleich
           </p>
           <p>
             <strong>Empfundene Temperatur-Werte:</strong> Sehr kalt (&lt;6°C), Kalt (6-11°C), Kühl (11-15°C), Leicht kühl (15-19°C), Behaglich (19-22°C), Leicht warm (22-26°C), Warm (26-32°C), Heiss (32-37°C), Sehr heiss (&gt;37°C)
           </p>
         </div>
       </div>
    </div>
  );
};

export default TemperatureDevelopmentChart;
